import { Table, Button } from 'react-bootstrap';
import { useGetStoreStocksQuery } from '../../slices/storeStocksApiSlice';
import { useGetStoreSuppliesQuery } from '../../slices/storeSuppliesApiSlice';
import { useGetStoreTransfersQuery } from '../../slices/storeTransfersApiSlice';
import { useGetStoreExpensesQuery } from '../../slices/storeExpensesApiSlice';
import { useGetStoreIncomesQuery } from '../../slices/storeIncomesApiSlice';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { useState, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';

const StoreWorkingCapital = () => {
  const { data: storeStocksData, isLoading, error } = useGetStoreStocksQuery();
  const { data: storeSuppliesData } = useGetStoreSuppliesQuery();
  const { data: storeTransfersData } = useGetStoreTransfersQuery();

  const storeStocks = useMemo(() => storeStocksData?.storeStocks || [], [storeStocksData]);
  const storeSupplies = useMemo(() => storeSuppliesData?.storeSupplies || [], [storeSuppliesData]);
  const storeTransfers = useMemo(() => storeTransfersData?.storeTransfers || [], [storeTransfersData]);

  const [storeStockValues, setStoreStockValues] = useState({});

  const uniqueDates = useMemo(() => {
    return storeStocks.length > 0
      ? [...new Set(storeStocks.map(s => new Date(s.date).toISOString().split('T')[0]))]
      : [];
  }, [storeStocks]);

  const sortedDates = useMemo(
    () => uniqueDates.sort((a, b) => new Date(a) - new Date(b)),
    [uniqueDates]
  );

  const date1 = sortedDates;
  const date2 = [...sortedDates.slice(1), sortedDates[sortedDates.length - 1]];

  const { data: storeExpensesData } = useGetStoreExpensesQuery({
    startDate: date1,
    endDate: date2,
  });

  const { data: storeIncomesData } = useGetStoreIncomesQuery({
    startDate: date1,
    endDate: date2,
  });

  const storeExpenses = useMemo(() => storeExpensesData?.storeExpenses || [], [storeExpensesData]);
  const storeIncomes = useMemo(() => storeIncomesData?.storeIncomes || [], [storeIncomesData]);

  // -----------------------------
  // CALCULATION FUNCTIONS
  // -----------------------------

  const calcStoreStockValue = useCallback(
    date => {
      return storeStocks
        .filter(s => new Date(s.date).toISOString().split('T')[0] === date)
        .flatMap(s => s.storeStockItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeStocks]
  );

  const calcStoreSupplyValue = useCallback(
    (start, cutoff) => {
      return storeSupplies
        .filter(s => {
          const d = new Date(s.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(s => s.storeSupplyItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeSupplies]
  );

  const calcStoreTransferValue = useCallback(
    (start, cutoff) => {
      return storeTransfers
        .filter(t => {
          const d = new Date(t.date).toISOString().split('T')[0];
          return d >= start && d <= cutoff;
        })
        .flatMap(t => t.storeTransferItems || [])
        .reduce((acc, item) => acc + item.price * item.qty, 0);
    },
    [storeTransfers]
  );

  const calcStoreExpenseValue = useCallback(
    (start, cutoff) => {
      return storeExpenses
        .filter(e => {
          const d = new Date(e.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, e) => acc + Number(e.amount), 0);
    },
    [storeExpenses]
  );

  const calcStoreIncomeValue = useCallback(
    (start, cutoff) => {
      return storeIncomes
        .filter(i => {
          const d = new Date(i.date).getTime();
          return d >= new Date(start).getTime() && d <= new Date(cutoff).getTime();
        })
        .reduce((acc, i) => acc + Number(i.amount), 0);
    },
    [storeIncomes]
  );

  const calcStoreSalesCredit = (start, cutoff) => {
    return storeExpenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return e.category === 'SalesCredit' && d >= start && d <= cutoff;
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  const calcStoreSalesPayment = (start, cutoff) => {
    return storeExpenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return e.category === 'SalesPayment' && d >= start && d <= cutoff;
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  const calcCumulativeStoreReceivable = (start, cutoff) => {
    return storeExpenses
      .filter(e => {
        const d = new Date(e.date).toISOString().split('T')[0];
        return (
          d >= start &&
          d <= cutoff &&
          (e.category === 'SalesCredit' || e.category === 'SalesPayment')
        );
      })
      .reduce((sum, e) => sum + Number(e.amount), 0);
  };

  // -----------------------------
  // MAIN RECALCULATION LOGIC
  // -----------------------------

  const recalc = () => {
    const updated = date1.reduce((acc, date, index) => {
      const start = date;
      const end = date2[index];

      // cutoff = endDate - 1 day
      const cutoff = new Date(end);
      cutoff.setDate(cutoff.getDate() - 1);
      const cutoffDate = cutoff.toISOString().split('T')[0];

      const opening = calcStoreStockValue(start);
      const supply = calcStoreSupplyValue(start, cutoffDate);
      const transfer = calcStoreTransferValue(start, cutoffDate);
      const expense = calcStoreExpenseValue(start, cutoffDate);
      const income = calcStoreIncomeValue(start, cutoffDate);
      const credit = calcStoreSalesCredit(start, cutoffDate);
      const payment = calcStoreSalesPayment(start, cutoffDate);
      const closing = calcStoreStockValue(end);

      const net = opening + supply + expense - transfer - closing;
      const varValue = income - net;

      const cumulative = calcCumulativeStoreReceivable(date1[0], cutoffDate);

      acc[date] = {
        openingStoreStockValue: opening,
        storeSupplyValue: supply,
        storeTransferValue: transfer,
        storeExpenseValue: expense,
        storeIncomeValue: income,
        storeExpenseSalesCreditValue: credit,
        storeExpenseSalesPaymentValue: payment,
        increaseInReceivables: credit + payment,
        closingStoreStockValue: closing,
        netValue: net,
        varValue,
        reValue: cumulative,
      };

      return acc;
    }, {});

    setStoreStockValues(updated);
  };

  // -----------------------------
  // EXPORT TO EXCEL
  // -----------------------------

  const exportToExcel = () => {
    const tableData = date1.map((date, index) => {
      const v = storeStockValues[date] || {};
      return {
        Date1: date,
        Date2: date2[index],
        'Opening StoreStock Value': v.openingStoreStockValue,
        'StoreSupply Value': v.storeSupplyValue,
        'StoreTransfer Value': v.storeTransferValue,
        'StoreExpense Value': v.storeExpenseValue,
        'Closing StoreStock Value': v.closingStoreStockValue,
        'Net Value': v.netValue,
        'StoreIncome Value': v.storeIncomeValue,
        'Var': v.varValue,
        'Credit': v.storeExpenseSalesCreditValue,
        'Payment': v.storeExpenseSalesPaymentValue,
        'Receivable Inc': v.increaseInReceivables,
        'RVar': v.reValue,
      };
    });

    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Store Working Capital');
    XLSX.writeFile(wb, 'StoreWorkingCapital.xlsx');
  };

  // -----------------------------
  // RENDER
  // -----------------------------

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message}</Message>;
  if (storeStocks.length === 0) return <Message>No store stock data</Message>;

  return (
    <div>
      <h1>Store Working Capital</h1>
      <Button onClick={recalc}>Recalculate</Button>

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>Date1</th>
            <th>Date2</th>
            <th>Opening</th>
            <th>Supply</th>
            <th>Transfer</th>
            <th>Expense</th>
            <th>Closing</th>
            <th>Net</th>
            <th>Income</th>
            <th>Var</th>
            <th>Credit</th>
            <th>Payment</th>
            <th>Receivable Inc</th>
            <th>RVar</th>
          </tr>
        </thead>

        <tbody>
          {date1.map((date, index) => {
            const v = storeStockValues[date] || {};
            return (
              <tr key={date}>
                <td>{date}</td>
                <td>{date2[index]}</td>
                <td>{v.openingStoreStockValue}</td>
                <td>{v.storeSupplyValue}</td>
                <td>{v.storeTransferValue}</td>
                <td>{v.storeExpenseValue}</td>
                <td>{v.closingStoreStockValue}</td>
                <td>{v.netValue}</td>
                <td>{v.storeIncomeValue}</td>
                <td>{v.varValue}</td>
                <td>{v.storeExpenseSalesCreditValue}</td>
                <td>{v.storeExpenseSalesPaymentValue}</td>
                <td>{v.increaseInReceivables}</td>
                <td>{v.reValue}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Button onClick={exportToExcel}>Export to Excel</Button>
    </div>
  );
};

export default StoreWorkingCapital;
